import { useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Avatar,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import DashboardIcon from "@mui/icons-material/Dashboard";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import BarChartIcon from "@mui/icons-material/BarChart";
import PaymentIcon from "@mui/icons-material/Payment";
import DescriptionIcon from "@mui/icons-material/Description";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import LogoutIcon from "@mui/icons-material/Logout";
import SendIcon from "@mui/icons-material/Send";
import SettingsIcon from "@mui/icons-material/Settings";
import ThemeToggle from "@/components/ThemeToggle";
import { useAuth } from "@/contexts/AuthContext";
import { minHeightDvh, safeAreaTop, safeAreaX } from "@/utils/viewport";
import UnverifiedBanner from "@/components/UnverifiedBanner";

export const DRAWER_WIDTH = 260;
export const DESKTOP_BREAKPOINT_PX = 900;

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
}

const NAV_ITEMS: NavItem[] = [
  { label: "Overview", path: "/dashboard/overview", icon: <DashboardIcon /> },
  { label: "API Keys", path: "/dashboard/api-keys", icon: <VpnKeyIcon /> },
  {
    label: "Usage & History",
    path: "/dashboard/usage",
    icon: <BarChartIcon />,
  },
  { label: "Billing", path: "/dashboard/billing", icon: <PaymentIcon /> },
  { label: "Webhooks", path: "/dashboard/webhooks", icon: <SendIcon /> },
  { label: "Settings", path: "/dashboard/settings", icon: <SettingsIcon /> },
];

export default function DashboardLayout() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavClick = (path: string) => {
    navigate(path);
    if (isMobile) setMobileOpen(false);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleMenuClose();
    await logout();
    navigate("/login");
  };

  const activeGradient = isDark
    ? "linear-gradient(135deg, rgba(124,58,237,0.4), rgba(37,99,235,0.4))"
    : "linear-gradient(135deg, #7c3aed, #2563eb)";

  const logoGradient = isDark
    ? "linear-gradient(135deg, #a78bfa, #60a5fa)"
    : "linear-gradient(135deg, #7c3aed, #2563eb)";

  const drawerContent = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Logo */}
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 2 }}
      >
        <Box
          component="img"
          src="/logo.svg"
          alt="PDFGlyph"
          sx={{
            width: 24,
            height: 24,
            flexShrink: 0,
          }}
        />
        <Typography variant="h6" noWrap sx={{ fontWeight: 800 }}>
          PDFGlyph
        </Typography>
      </Box>
      <Divider />
      {/* Nav items */}
      <List sx={{ flex: 1, px: 1, pt: 1 }}>
        {NAV_ITEMS.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <ListItemButton
              key={item.path}
              selected={isActive}
              onClick={() => handleNavClick(item.path)}
              sx={{
                borderRadius: "12px",
                mb: 0.5,
                ...(isActive && {
                  background: activeGradient,
                  color: "white",
                  ...(isDark && {
                    border: "1px solid rgba(124,58,237,0.3)",
                  }),
                  "&:hover": {
                    background: activeGradient,
                    opacity: 0.9,
                  },
                  "& .MuiListItemIcon-root": {
                    color: "white",
                  },
                }),
                "&.Mui-selected": {
                  background: activeGradient,
                  color: "white",
                  ...(isDark && {
                    border: "1px solid rgba(124,58,237,0.3)",
                  }),
                  "&:hover": {
                    background: activeGradient,
                    opacity: 0.9,
                  },
                  "& .MuiListItemIcon-root": {
                    color: "white",
                  },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          );
        })}
      </List>
      <Divider />
      {/* API Docs link */}
      <List sx={{ px: 1 }}>
        <ListItemButton
          component="a"
          href="/api-docs/"
          target="_blank"
          sx={{ borderRadius: "12px", mb: 0.5 }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <DescriptionIcon />
          </ListItemIcon>
          <ListItemText primary="API Docs" />
          <OpenInNewIcon sx={{ fontSize: 16, color: "text.secondary" }} />
        </ListItemButton>
      </List>
      <Divider />
      {/* User info */}
      <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
        <Avatar
          sx={{
            width: 32,
            height: 32,
            background: logoGradient,
            fontSize: 14,
          }}
        >
          {user?.email?.charAt(0).toUpperCase()}
        </Avatar>
        <Box>
          <Typography
            variant="caption"
            sx={{ display: "block", fontWeight: 600 }}
          >
            {user?.email}
          </Typography>
          <Typography variant="caption" display="block" color="text.secondary">
            Plan: {user?.plan?.charAt(0).toUpperCase()}
            {user?.plan?.slice(1)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", ...minHeightDvh }}>
      <Box
        component="a"
        href="#main-content"
        sx={{
          position: "absolute",
          left: 8,
          top: -40,
          zIndex: 9999,
          px: 2,
          py: 1,
          bgcolor: "background.paper",
          color: "primary.main",
          borderRadius: "4px",
          textDecoration: "none",
          fontWeight: 600,
          "&:focus": {
            top: 8,
            outline: "2px solid",
            outlineColor: "primary.main",
            outlineOffset: 2,
          },
        }}
      >
        Skip to content
      </Box>
      <Box
        component="nav"
        sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: DRAWER_WIDTH,
              bgcolor: theme.palette.background.default,
              borderRight: "1px solid",
              borderColor: theme.palette.divider,
            },
          }}
        >
          {drawerContent}
        </Drawer>
        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", md: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: DRAWER_WIDTH,
              bgcolor: theme.palette.background.default,
              borderRight: "1px solid",
              borderColor: theme.palette.divider,
            },
          }}
          open
        >
          {drawerContent}
        </Drawer>
      </Box>

      <Box
        id="main-content"
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { xs: "100%", md: `calc(100% - ${DRAWER_WIDTH}px)` },
          maxWidth: "100vw",
          overflowX: "hidden",
          bgcolor: "background.default",
          ...minHeightDvh,
          ...safeAreaTop,
          ...safeAreaX,
        }}
      >
        {/* Top bar */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          {/* Left side: hamburger + title */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              color="inherit"
              edge="start"
              aria-label="Open navigation menu"
              onClick={handleDrawerToggle}
              sx={{ display: { md: "none" } }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Dashboard
            </Typography>
          </Box>
          {/* Right side: theme toggle + avatar */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ThemeToggle />
            <IconButton
              onClick={handleMenuOpen}
              size="small"
              aria-label="Open account menu"
              aria-haspopup="true"
              aria-expanded={Boolean(anchorEl)}
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  background: logoGradient,
                  fontSize: 14,
                }}
              >
                {user?.email?.charAt(0).toUpperCase()}
              </Avatar>
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <MenuItem disabled>
                <Typography variant="body2">{user?.email}</Typography>
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                Sign Out
              </MenuItem>
            </Menu>
          </Box>
        </Box>

        {user && !user.emailVerifiedAt && (
          <UnverifiedBanner email={user.email} />
        )}
        <Outlet />
      </Box>
    </Box>
  );
}
